import { HttpClient } from '@angular/common/http';
import {Injectable, signal} from '@angular/core';
import {Observable} from 'rxjs';
import {catchError, finalize, tap} from 'rxjs/operators';
import {ApiHelper} from './api.helper';
import {LoaderService} from './loader.service';
import {ToolsService} from './tools.service';
import {RedirectService} from './redirect.service';
import {Careprovider} from '../types/Careprovider';
import {SearchResultCareProviders} from '../types/SearchResultCareProviders';
import {SearchResult} from '../types/SearchResult';
import {SearchResultsPayload} from '../types/SearchResultsPayload';
import {RoutesEnums} from '../enums/RoutesEnums';
import {SearchResultsCareproviderUnitItem} from '../types/SearchResultsCareproviderUnitItem';
import {DefaultPage} from '../app-common/const';
import {SortConditionsFieldsEnum} from '../enums/SearchEnums';
import {CaregiverCpruSearchResults} from '../types/CaregiverCpruSearchResults';
import {LoaderQueueItem} from '../types/LoaderQueueItem';
import {AppConfigService} from './app-config.service';

@Injectable({
  providedIn: 'root',
})
export class CaregiverService {
  public cpruItems = signal<SearchResultsCareproviderUnitItem[]>([]);
  public careprovider = signal<Careprovider>(null);
  public searchPayload = signal<SearchResultsPayload>(this.getDefaultSearchPayload());
  public hasNextPage = signal<boolean>(false);

  private pageSize: number = 6;
  private nextPage: number = 0;
  private page: number = 1;
  private isLoadingFromCareproviderEndpoint: boolean = true;

  constructor(
    private http: HttpClient,
    private apiHelper: ApiHelper,
    private tools: ToolsService,
    private redirect: RedirectService,
    private loaderService: LoaderService,
    private appConfigService: AppConfigService,
  ) {}

  public getCareproviderById(id: number): Observable<Careprovider> {
    let endpoint: string = this.appConfigService.appConfig.apiUrl + `caregiver/${id}`;

    return this.http.get<Careprovider>(endpoint).pipe(
      tap((results) => this.handleCareprovider(results)),
      tap((results) => this.conditional404Redirect(results)),
      tap((results) => this.handleCareproviderUnitsFromCareProvider(results.caregiverUnits)),
      catchError((error) => this.apiHelper.handleError(error)),
    );
  }

  public getCareproviderUnitsResults(id: number, page: number): Observable<CaregiverCpruSearchResults> {
    let endpoint: string =
      this.appConfigService.appConfig.apiUrl + `caregiver/${id}/units?page=${page}&pageSize=${this.pageSize}`;
    return this.http.get<CaregiverCpruSearchResults>(endpoint).pipe(
      tap((results) => this.handleCareproviderUnitsFromCareProvider(results)),
      catchError((error) => this.apiHelper.handleError(error)),
    );
  }

  public getSearchResults(payload: SearchResultsPayload): Observable<SearchResult> {
    const params = this.tools.buildHttpParams(payload);

    return this.http
      .get<SearchResult>(this.appConfigService.appConfig.apiUrl + 'search', {
        params: params,
      })
      .pipe(
        tap((results) => this.handleCpruSearchResults(results.careProviders)),
        catchError((error) => this.apiHelper.handleError(error)),
      );
  }

  public loadMoreCprus(): void {
    if (this.hasNextPage()) {
      this.page = this.nextPage;
      this.searchPayload.update((payload) => {
        payload.page = this.page;
        return payload;
      });

      if (this.isLoadingFromCareproviderEndpoint) {
        this.loadCprusFromMainList();
      } else {
        this.loadCprusFromFilteredList();
      }
    }
  }

  public updatePlaceFilter(placeUrlPath: string): void {
    this.page = 1;
    this.cpruItems.set([]);
    this.searchPayload.update((payload) => {
      payload.placesUrlPath = placeUrlPath;
      payload.page = this.page;
      return payload;
    });
    this.isLoadingFromCareproviderEndpoint = false;
    this.loadCprusFromFilteredList();
  }

  public updateCategoryFilter(categoryUrlPath: string): void {
    this.page = 1;

    this.searchPayload.update((payload) => {
      payload.caregiverUnitTypesUrlPath = categoryUrlPath;
      payload.page = this.page;
      return payload;
    });
    this.cpruItems.set([]);
    this.isLoadingFromCareproviderEndpoint = false;
    this.loadCprusFromFilteredList();
  }

  private handleCareprovider(result: Careprovider): void {
    this.isLoadingFromCareproviderEndpoint = true;
    this.careprovider.set(result);
    this.resetPayload(result.urlPath);
    this.resetPageNumber();
    this.nextPage = result.caregiverUnits.nextPage;
    this.hasNextPage.set(result.caregiverUnits.hasNextPage);
  }

  private handleCareproviderUnitsFromCareProvider(result: CaregiverCpruSearchResults): void {
    this.cpruItems.update((items) => items.concat(result.items));
    this.nextPage = result.nextPage;
    this.hasNextPage.set(result.hasNextPage);
  }

  private loadCprusFromMainList(): void {
    const queryId: number = this.loaderService.generateQueueId();
    const queryItem: LoaderQueueItem = {
      id: queryId,
      title: 'Laddar resultat...',
      description: '',
    };
    this.loaderService.addToQueue(queryItem);

    this.getCareproviderUnitsResults(this.careprovider().id, this.page)
      .pipe(finalize(() => this.loaderService.removeFromQueue(queryId)))
      .subscribe();
  }

  private loadCprusFromFilteredList(): void {
    const queryId: number = this.loaderService.generateQueueId();
    const queryItem: LoaderQueueItem = {
      id: queryId,
      title: 'Laddar resultat...',
      description: '',
    };
    this.loaderService.addToQueue(queryItem);

    this.getSearchResults(this.searchPayload())
      .pipe(finalize(() => this.loaderService.removeFromQueue(queryId)))
      .subscribe();
  }

  private resetPayload(careproviderUrlPatch: string): void {
    this.searchPayload.update((payload) => {
      payload.caregiverUrlPath = careproviderUrlPatch;
      return payload;
    });
  }

  private resetPageNumber(): void {
    this.page = 1;
    this.searchPayload.update((payload) => {
      payload.page = 1;
      return payload;
    });
  }

  private handleCpruSearchResults(results: SearchResultCareProviders): void {
    this.nextPage = results.totalPages > results.page ? results.page + 1 : results.page;
    this.hasNextPage.set(results.totalPages > results.page);
    this.cpruItems.update((items) => items.concat(results.hits));
  }

  private conditional404Redirect(results: Careprovider): void {
    if (this.tools.isNil(results)) {
      this.redirect.redirect(['/', RoutesEnums.NOTFOUND]);
    }
  }

  private getDefaultSearchPayload(): SearchResultsPayload {
    return {
      page: DefaultPage,
      pageSize: this.pageSize,
      urlPath: '',
      caregiverUnitTypesUrlPath: '',
      symptomUrlPath: '',
      placesUrlPath: '',
      careServicesTypeUrlPath: '',
      freeSearch: '',
      openNow: false,
      openAtWeekend: false,
      location: {
        lat: 0,
        lon: 0,
      },
      sorting: SortConditionsFieldsEnum.ES_SCORE,
      customerFeatureTypes: [],
      digitalPage: DefaultPage,
      caregiverUrlPath: '',
      includeSearchResult: true,
      includeAdCampaignOwner: false,
    };
  }
}
